import { compareAsc, isEqual, formatDistance } from 'date-fns'

import { FeatureFlagId } from '../feature-flags/feature-flags.interface'

import {
  ProgramEnrolmentStatusId,
  ScheduleType,
  ProgramInteractionTypeId,
} from './program.interface'

import type {
  IInteractionDate} from './program.interface';

export const getScheduleTypeLabelFromTypeId = (scheduleType: ScheduleType) => {
  switch (scheduleType) {
    case ScheduleType.DaysInAdvance:
      return 'Days In Advance'

    case ScheduleType.DaysPastDue:
      return 'Days Past Due'
  }
}

export const getScheduleLabel = (scheduleType: ScheduleType) =>
  getScheduleTypeLabelFromTypeId(scheduleType)

export const getStatusLabelFromStatusId = (
  status: ProgramEnrolmentStatusId,
) => {
  switch (status) {
    case ProgramEnrolmentStatusId.Enroled:
      return 'Enroled'

    case ProgramEnrolmentStatusId.Discontinued:
      return 'Discontinued'

    case ProgramEnrolmentStatusId.Removed:
      return 'Removed'

    default:
      return ''
  }
}

export const getStatusLabel = (status: ProgramEnrolmentStatusId) =>
  getStatusLabelFromStatusId(status)

export const getInteractionExpectedCompletionDateLabelFromType = (
  currentDate: Date,
  expectedCompletionDate: IInteractionDate,
) => {
  const interactionExpected = new Date(expectedCompletionDate).getTime()

  if (isEqual(currentDate.getTime(), interactionExpected) === true) {
    return 'Expected completion today'
  }

  const expectedCompletion = formatDistance(
    interactionExpected,
    currentDate.getTime(),
    {
      addSuffix: true,
    },
  )

  return 'Expected completion ' + expectedCompletion
}

export const getInteractionCompletedDateLabelFromTypes = (
  expectedCompletionDate: IInteractionDate,
  interactionCompletedAt: IInteractionDate,
) => {
  const interactionCompleted = new Date(interactionCompletedAt).getTime()
  const interactionExpected = new Date(expectedCompletionDate).getTime()

  if (isEqual(interactionCompleted, interactionExpected) === true) {
    return 'Completed on expected day'
  } else {
    const completionDeltaString = formatDistance(
      interactionCompleted,
      interactionExpected,
    )

    const completionDeltaAdjective = compareAsc(
      interactionCompleted,
      interactionExpected,
    )
    const daysBeforeorAfter = completionDeltaAdjective > 0 ? 'after' : 'before'

    return (
      'Completed ' +
      completionDeltaString +
      ' ' +
      daysBeforeorAfter +
      ' expected'
    )
  }
}

export const getInteractionExpectedCompletionDateLabel = (
  currentDate: Date,
  expectedCompletionDate: IInteractionDate,
) =>
  getInteractionExpectedCompletionDateLabelFromType(
    currentDate,
    expectedCompletionDate,
  )

export const getInteractionCompletedDateLabel = (
  expectedCompletionDate: IInteractionDate,
  interactionCompletedAt: IInteractionDate,
) =>
  getInteractionCompletedDateLabelFromTypes(
    expectedCompletionDate,
    interactionCompletedAt,
  )

export const getInteractionTypeLabelFromTypeId = (
  interactionType: ProgramInteractionTypeId,
) => {
  switch (interactionType) {
    case ProgramInteractionTypeId.InitialInteraction:
      return 'Initial'
    case ProgramInteractionTypeId.AdHocInteraction:
      return 'Ad Hoc'
    case ProgramInteractionTypeId.FollowUpInteraction:
      return 'Follow Up'
    case ProgramInteractionTypeId.RemoteInteraction:
      return 'Remote'
  }
}

export const getInteractionLabel = (
  interactionType: ProgramInteractionTypeId,
) => getInteractionTypeLabelFromTypeId(interactionType)

export const featureFlagToInteractionTypeMap: Partial<Record<
  FeatureFlagId,
  ProgramInteractionTypeId[]
>> = {
  [FeatureFlagId.InitialAndFollowUpInteraction]: [
    ProgramInteractionTypeId.InitialInteraction,
    ProgramInteractionTypeId.FollowUpInteraction,
  ],
  [FeatureFlagId.AdHocInteraction]: [ProgramInteractionTypeId.AdHocInteraction],
  [FeatureFlagId.RemoteInteraction]: [
    ProgramInteractionTypeId.RemoteInteraction,
  ],
}

export const filterInteractionsByFeatureFlags = <T>(
  interactions: T[],
  enabledFeatureFlags: FeatureFlagId[],
  getInteractionType: (interaction: T) => ProgramInteractionTypeId,
): T[] => {
  const allowedTypes = new Set<ProgramInteractionTypeId>(
    enabledFeatureFlags.flatMap(
      (flag) => featureFlagToInteractionTypeMap[flag] || [],
    ),
  )

  return interactions.filter((interaction) =>
    allowedTypes.has(getInteractionType(interaction)),
  )
}

/**
 * Constructs an ISO 8601 duration string from a quantity and unit.
 * Note: This function only supports single-component durations (e.g., "P30D", "PT2H").
 * It does not handle full ISO 8601 durations with multiple components (e.g., "P1Y2M10D").
 */
export const constructIsoDuration = (
  quantity: number,
  unit: string,
  type: 'date' | 'time',
): string => {
  const normalizedUnit = unit === 'MO' ? 'M' : unit
  return type === 'date'
    ? `P${quantity}${normalizedUnit}`
    : `PT${quantity}${normalizedUnit}`
}

/**
 * Parses an ISO 8601 duration string into a structured format.
 * Note: This function only supports single-component durations (e.g., "P30D", "PT2H").
 * It does not handle full ISO 8601 durations with multiple components (e.g., "P1Y2M10D").
 */
export const parseIsoDuration = (isoString: string) => {
  const timeMatch = isoString.match(/^PT(\d+)(M|H)$/)
  const dateMatch = isoString.match(/^P(\d+)(D|W|M)$/)

  if (timeMatch) {
    return {
      quantity: parseInt(timeMatch[1], 10),
      unit: timeMatch[2],
      type: 'time' as const,
    }
  } else if (dateMatch) {
    return {
      quantity: parseInt(dateMatch[1], 10),
      unit: dateMatch[2] === 'M' ? 'MO' : dateMatch[2],
      type: 'date' as const,
    }
  }

  return { quantity: 30, unit: 'D', type: 'date' as const }
}
